import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const GARNET = '#5b1e22';
const COAL = '#181617';
const ASHEN = '#7A766F';
const RED = '#b32d2d';
const GREEN = '#1a7a4c';

function fmtDate(d) {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function groupByStudio(closures) {
  const map = {};
  for (const c of closures) {
    if (!map[c.studio_id]) map[c.studio_id] = { name: c.studio_name, dates: [] };
    map[c.studio_id].dates.push(c);
  }
  return map;
}

export default function StudioClosures() {
  const [studios, setStudios] = useState([]);
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [toastError, setToastError] = useState(false);

  // Form state
  const [studioId, setStudioId] = useState('');
  const [mode, setMode] = useState('range'); // 'single' | 'range'
  const [singleDate, setSingleDate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [studiosRes, closuresRes] = await Promise.all([
        api.get('/studios'),
        api.get('/booking/closures'),
      ]);
      setStudios(Array.isArray(studiosRes.data) ? studiosRes.data : (studiosRes.data.studios || []));
      setClosures(closuresRes.data.closures || []);
      if (!studioId && (studiosRes.data.studios || []).length > 0) {
        setStudioId(studiosRes.data.studios[0].id);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [studioId]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  function showToast(msg, isError = false) {
    setToast(msg);
    setToastError(isError);
    setTimeout(() => setToast(''), 3500);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { studio_id: studioId, reason: reason || null };
      if (mode === 'single') {
        body.date = singleDate;
      } else {
        body.from_date = fromDate;
        body.to_date = toDate;
      }
      const res = await api.post('/booking/closures', body);
      showToast(res.data.message);
      setSingleDate(''); setFromDate(''); setToDate(''); setReason('');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save closures', true);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, dateLabel) {
    if (!window.confirm(`Remove closure for ${dateLabel}?`)) return;
    try {
      await api.delete(`/booking/closures/${id}`);
      showToast('Closure removed');
      setClosures(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to remove', true);
    }
  }

  const grouped = groupByStudio(closures);

  return (
    <div>
      {toast && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          background: toastError ? RED : GREEN, color: '#fff', padding: '10px 20px',
          borderRadius: 8, fontSize: 14, fontWeight: 600, zIndex: 2000,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        }}>
          {toast}
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, color: COAL, fontSize: 22 }}>Studio Closed Dates</h2>
        <div style={{ fontSize: 13, color: ASHEN, marginTop: 4 }}>
          Mark dates as closed — customers cannot book on closed days
        </div>
      </div>

      {/* Add Closures Form */}
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '20px 20px', marginBottom: 28 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 16 }}>
          Close Dates
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Studio</label>
              <select
                value={studioId}
                onChange={e => setStudioId(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, color: COAL }}
                required
              >
                {studios.map(s => <option key={s.id} value={s.id}>{s.name} ({s.country})</option>)}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Type</label>
              <select
                value={mode}
                onChange={e => setMode(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, color: COAL }}
              >
                <option value="range">Date range</option>
                <option value="single">Single date</option>
              </select>
            </div>

            {mode === 'single' ? (
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Date</label>
                <input
                  type="date"
                  value={singleDate}
                  min={today}
                  onChange={e => setSingleDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, color: COAL, boxSizing: 'border-box' }}
                  required
                />
              </div>
            ) : (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>From</label>
                  <input
                    type="date"
                    value={fromDate}
                    min={today}
                    onChange={e => setFromDate(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, color: COAL, boxSizing: 'border-box' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>To</label>
                  <input
                    type="date"
                    value={toDate}
                    min={fromDate || today}
                    onChange={e => setToDate(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, color: COAL, boxSizing: 'border-box' }}
                    required
                  />
                </div>
              </>
            )}

            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Reason (shown to customers)</label>
              <input
                type="text"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. Public holiday, Pre-opening period"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, color: COAL, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '9px 20px', background: GARNET, color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Mark as Closed'}
          </button>
        </form>
      </div>

      {/* Upcoming Closures */}
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 12 }}>
        Upcoming Closures
      </div>

      {loading ? (
        <div style={{ color: ASHEN, fontSize: 14 }}>Loading…</div>
      ) : closures.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '20px', color: ASHEN, fontSize: 14 }}>
          No upcoming closed dates.
        </div>
      ) : (
        Object.entries(grouped).map(([sid, group]) => (
          <div key={sid} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: 700, fontSize: 14, color: COAL }}>
              {group.name}
            </div>
            <div style={{ padding: '4px 0' }}>
              {group.dates.map(c => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #f8f8f8' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COAL }}>{fmtDate(c.date)}</div>
                    {c.reason && <div style={{ fontSize: 12, color: ASHEN, marginTop: 2 }}>{c.reason}</div>}
                  </div>
                  <button
                    onClick={() => handleDelete(c.id, fmtDate(c.date))}
                    style={{ background: 'none', border: '1px solid #eee', borderRadius: 6, padding: '4px 10px', fontSize: 12, color: ASHEN, cursor: 'pointer' }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
